import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography, Box, CircularProgress } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { queryApi } from 'src/api/queryApi';
import { reportsApi } from 'src/api/reportsApi';
import { updateExternalEffortInReport } from 'src/utils/reportUtils';
import { formatDateTimeForServer } from 'src/utils/dateUtils';
import { parseQueryNotes, updateQueryNotesField } from 'src/utils/queryNotesUtils';
import { getQueryDateForDisplay } from 'src/utils/queryUtils';

const DateEditModal = ({ open, onClose, effort, isExternal, onSuccess, company, dateRanges, dateFilter, allReports, allReportsMutate }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize the date when modal opens
  useEffect(() => {
    if (open && effort) {
      // Use the same date that's displayed in EffortRow
      const currentDate = isExternal ? effort.date : getQueryDateForDisplay(effort);

      // For regular queries, check notes field for reportDate
      let reportDate = null;
      if (!isExternal && effort.notes) {
        const parsedNotes = parseQueryNotes(effort.notes);
        reportDate = parsedNotes.reportDate;
      }
      // For external efforts, check reportDate field directly
      else if (isExternal && effort.reportDate) {
        reportDate = effort.reportDate;
      }

      if (reportDate) {
        setSelectedDate(moment(reportDate).format('YYYY-MM-DDTHH:mm'));
      } else {
        setSelectedDate(moment(currentDate).format('YYYY-MM-DDTHH:mm'));
      }
    }
  }, [open, effort, isExternal]);

  const handleSubmitRegularQuery = async () => {
    const formattedDate = formatDateTimeForServer(selectedDate);

    // Update reportDate in the notes JSON
    const updatedNotes = updateQueryNotesField(effort.notes, 'reportDate', formattedDate);

    await queryApi.updateQueryV2({
      queryID: effort.id,
      title: effort.title,
      description: effort.description,
      status: effort.status,
      source: effort.source,
      topics: effort.topics,
      createdBy: effort.userClaimID,
      notes: updatedNotes,
    });
  };

  const handleSubmitExternalEffort = async () => {
    const formattedDate = formatDateTimeForServer(selectedDate);

    // Use the utility function to update external effort
    await updateExternalEffortInReport(reportsApi, {
      company,
      dateRanges,
      dateFilter,
      allReports,
      effortId: effort.id,
      updatedFields: {
        ...effort,
        date: formattedDate,
        reportDate: formattedDate,
      },
    });

    // Refresh reports data
    allReportsMutate();
  };

  const handleSubmit = async () => {
    if (!selectedDate || !effort) return;

    setIsSubmitting(true);
    try {
      if (isExternal) {
        await handleSubmitExternalEffort();
        toast.success('External effort date updated successfully');
      } else {
        await handleSubmitRegularQuery();
        toast.success('Report date updated successfully');
      }

      if (onSuccess) {
        const formattedDate = formatDateTimeForServer(selectedDate);
        onSuccess(effort.id, formattedDate);
      }
      onClose();
    } catch (error) {
      toast.error('Failed to update date');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!effort) return null;

  // Use the same date that's displayed in EffortRow
  const originalDate = isExternal ? effort.date : getQueryDateForDisplay(effort);

  // Get current reportDate from appropriate source
  let currentReportDate = null;
  if (!isExternal && effort.notes) {
    const parsedNotes = parseQueryNotes(effort.notes);
    currentReportDate = parsedNotes.reportDate;
  } else if (isExternal && effort.reportDate) {
    currentReportDate = effort.reportDate;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Report Date</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Original Date:</strong> {moment(originalDate).format('MMM DD, YYYY HH:mm')}
          </Typography>
          {currentReportDate && (
            <Typography variant="body2" sx={{ mb: 1, color: 'primary.main' }}>
              <strong>Current Report Date:</strong> {moment(currentReportDate).format('MMM DD, YYYY HH:mm')}
            </Typography>
          )}
        </Box>

        <TextField label="Report Date" type="datetime-local" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting || !selectedDate} color="primary">
          {isSubmitting ? <CircularProgress size={20} /> : 'Update Date'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DateEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  effort: PropTypes.object,
  isExternal: PropTypes.bool,
  onSuccess: PropTypes.func,
  company: PropTypes.string,
  dateRanges: PropTypes.object,
  dateFilter: PropTypes.string,
  allReports: PropTypes.array,
  allReportsMutate: PropTypes.func,
};

export default DateEditModal;
